import React, { Component } from 'react';

import PaginationComponent from '../components/PaginationComponent';
import TableStateCard from '../components/TableStateCard';
import { sortClass, nextSortDir, sortAriaSort } from '../utils/table';
import { buildPaginationQuery, getPaginatedData, scheduleDebouncedCallback, clearDebouncedCallback } from '../utils/pagination';
import { hasActiveSearch, getSearchEmptyMessage } from '../utils/tableState';
import { navigateTo } from '../utils/navigation';

var config = process.env;

class TableUsersComponent extends Component {

    constructor() {
        super();
        this.state = {
            quantityShowRows: 25,
            currentPage: 1,
            totalItems: 0,
            search: '',
            searchInput: '',
            // Vacío a propósito: significa "el operador todavía no eligió columna", y es lo que
            // habilita el orden por relevancia del backend mientras busca. En cuanto hace clic
            // en una cabecera pasa a mandar la columna y su flecha manda sobre la relevancia.
            sortBy: '',
            sortDir: 'asc',
            data: [],
            loaded: false,
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    componentWillUnmount() {
        clearDebouncedCallback(this, 'searchDebounceTimeout');
    }

    fetchData = ({
        page = this.state.currentPage,
        limit = this.state.quantityShowRows,
        search = this.state.search,
        sortBy = this.state.sortBy,
        sortDir = this.state.sortDir,
        showLoader = true,
    } = {}) => {
        if (showLoader) {
            this.setState({ loaded: false });
        }
        var request = {
            method: 'GET',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            }
        };

        const query = buildPaginationQuery({ page, limit, search, sortBy, sortDir });
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}user?${query}`, request)
        .then(results => results.json())
        .then(data => {
            if (data.error > 0) {
                alert(data.message);
                if (data.error == 1002) {
                    window.location.href = localStorage.getItem('homePageURL');
                }
            } else {
                const paginatedData = getPaginatedData(data);
                this.setState({
                    data: paginatedData.items || [],
                    quantityShowRows: paginatedData.limit || Number(limit),
                    currentPage: paginatedData.page || Number(page),
                    totalItems: paginatedData.total || 0,
                    search,
                    sortBy,
                    sortDir,
                    loaded: true,
                });
            }
        });
    }

    drawRow = (item, index) => {
        // El listado une dos poblaciones: fichas de facturación (`user`, se crean al COMPRAR) y
        // cuentas de la tienda (`customer_account`, se crean al REGISTRARSE). Quien se registró
        // y reservó una mesa pero nunca compró sólo tiene lo segundo — por eso antes no aparecía
        // en ninguna búsqueda. Se marcan distinto porque no todo aplica: sin cédula no hay
        // compras ni crédito a los que apuntar.
        const detailQuery = item.cedula
            ? `cedula=${encodeURIComponent(item.cedula)}`
            : `accountId=${item.accountId}`;

        // La key sale de la PK de la tabla de la que vino la fila (cédula para las fichas, id para
        // las cuentas). Antes era `item.email || index` y en prod el email es literalmente '-' en
        // cientos de fichas: con keys repetidas React deja filas del listado ANTERIOR pegadas en
        // la grilla, así que al buscar se veían resultados mezclados con clientes que no
        // matcheaban nada. Es la mitad visible del "el buscador anda más o menos".
        const rowKey = item.source === 'cuenta' ? `cuenta:${item.accountId}` : `ficha:${item.cedula}`;

        return (
            <tr role="row" className="odd" key={rowKey} style={{ background: index % 2 == 0 ? '#ffffff;' : '#fcfcfc' }}>
                <td tabIndex="0" className="sorting_1">{item.firstName}</td>
                <td>{item.lastName}</td>
                <td>{item.phone || <span className="text-muted">—</span>}</td>
                <td>
                    {item.cedula || (
                        <span className="badge badge-light" title="Se registró en la tienda pero todavía no compró: no tiene ficha de facturación.">
                            Cuenta web
                        </span>
                    )}
                </td>
                <td>{item.email}</td>
                <td>{item.address || <span className="text-muted">—</span>}</td>
                <td className="text-right">
                    <button type="button" className="btn btn-sm btn-white" onClick={() => navigateTo(`/clientDetail?${detailQuery}`)}>
                        Ver detalle
                    </button>
                    {/* El crédito en tienda se lleva por cédula: sin ficha no hay a qué imputarlo. */}
                    {item.cedula && (
                        <button type="button" className="btn btn-sm btn-white ml-1" onClick={() => navigateTo(`/userCredit?cedula=${encodeURIComponent(item.cedula)}`)}>
                            Crédito
                        </button>
                    )}
                </td>
            </tr>
        );
    }

    loadData = () => {
        if (!this.state.loaded) {
            return <tbody></tbody>;
        }

        const items = this.state.data.map((item, index) => this.drawRow(item, index));
        if (items.length === 0) {
            items.push(
                <tr key="empty"><td colSpan="7" style={{ textAlign: 'center', padding: '10px', border: '1px solid rgb(225, 229, 235)' }}>
                    {getSearchEmptyMessage(this.state.searchInput || this.state.search)}
                </td></tr>
            );
        }

        return (
            <tbody>{items}</tbody>
        );
    }

    onChangeShowRows = () => {
        const itemSelected = Number(document.getElementById('select_rows').value);
        this.fetchData({ page: 1, limit: itemSelected });
    }

    onChangeInput = (search) => {
        this.setState({ searchInput: search });
        scheduleDebouncedCallback(this, 'searchDebounceTimeout', () => {
            this.fetchData({ page: 1, search, showLoader: false });
        });
    }

    orderData = (column) => {
        this.fetchData({
            page: 1,
            sortBy: column,
            sortDir: nextSortDir(column, this.state.sortBy, this.state.sortDir),
        });
    }

    // Props de una cabecera ordenable: la clase y el aria-sort se derivan del state, así que
    // sobreviven al remonte que provoca el loader.
    sortableHeader = (column) => ({
        onClick: () => this.orderData(column),
        className: sortClass(column, this.state.sortBy, this.state.sortDir),
        'aria-sort': sortAriaSort(column, this.state.sortBy, this.state.sortDir),
        tabIndex: '0',
    })

    render() {
        const searchActive = hasActiveSearch(this.state.searchInput, this.state.search);

        if (!this.state.loaded) {
            return <TableStateCard loading />;
        }

        if (this.state.totalItems === 0 && !searchActive) {
            return (
                <TableStateCard
                    icon="group"
                    title="Todavia no hay clientes para mostrar"
                    message="Cuando se registren clientes, aca vas a ver el listado con sus datos de contacto y direccion."
                />
            );
        }

        return (
            <div id="DataTables_Table_0_wrapper" className="dataTables_wrapper no-footer">
                <div className="dataTables_length" id="DataTables_Table_0_length">
                    <label>Show
                    <select id="select_rows" name="DataTables_Table_0_length" aria-controls="DataTables_Table_0" value={this.state.quantityShowRows} onChange={() => this.onChangeShowRows()}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    entries</label>
                </div>
                <div id="DataTables_Table_0_filter" className="dataTables_filter">
                    <label>Search:
                    {/* El placeholder es el único lugar donde el operador se entera de que puede
                        tipear varias cosas juntas: sin él, "Search" a secas se lee como "buscar por nombre". */}
                    <input id="input_search" type="search" className="" placeholder="Nombre, apellido, cédula, teléfono, correo…" style={{ width: '300px' }} aria-controls="DataTables_Table_0" value={this.state.searchInput} onChange={(e) => this.onChangeInput(e.target.value)}/>
                    </label>
                </div>

                <table className="transaction-history d-none dataTable no-footer dtr-inline" id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info">
                    <thead>
                        <tr role="row">
                            <th {...this.sortableHeader('firstName')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '12%' }}>Nombre</th>
                            <th {...this.sortableHeader('lastName')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '13%' }}>Apellido</th>
                            <th {...this.sortableHeader('phone')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '10%' }}>Teléfono</th>
                            <th {...this.sortableHeader('cedula')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '10%' }}>Cédula</th>
                            <th {...this.sortableHeader('email')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '25%' }}>Correo</th>
                            <th {...this.sortableHeader('address')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '25%' }}>Dirección</th>
                            <th className="sorting_disabled" rowSpan="1" colSpan="1" style={{ width: '5%' }}></th>
                        </tr>
                    </thead>
                    {this.loadData()}
                </table>

                <PaginationComponent
                    totalItems={this.state.totalItems}
                    quantityRows={this.state.quantityShowRows}
                    currentPage={this.state.currentPage}
                    onPageChange={(page) => this.fetchData({ page })}
                />
            </div>
        );
    }
}

export default TableUsersComponent;
